/** Connector exclusive_get_state truth — matches v11.0.166+ serde field. */
export type ExclusiveDsdOutputMode = 'dop' | 'decimated_pcm';

const DSD_RATE_TO_LABEL = new Map<number, string>([
  [2_822_400, 'DSD64'],
  [5_644_800, 'DSD128'],
  [11_289_600, 'DSD256'],
  [22_579_200, 'DSD512'],
]);

export function normalizeExclusiveDsdOutputMode(raw: unknown): ExclusiveDsdOutputMode | null {
  if (raw === 'dop') return 'dop';
  if (raw === 'decimated_pcm') return 'decimated_pcm';
  return null;
}

export function dsdLabelFromRateHz(hz: number): string {
  return DSD_RATE_TO_LABEL.get(hz) ?? 'DSD';
}

/** Decimated PCM carrier rate (DSD Hz / 8) as a display kHz label. */
export function decimatedPcmRateKhzLabel(sourceDsdHz: number): string {
  const pcmHz = sourceDsdHz / 8;
  const khz = pcmHz / 1000;
  return Number.isInteger(khz) ? String(khz) : khz.toFixed(1);
}

export interface FormatLocalNativeDsdBadgeInput {
  exclusiveDsdOutputMode: ExclusiveDsdOutputMode | null;
  exclusiveSourceDsdRateHz: number | null;
  /** From track metadata when poller has not reported source rate yet. */
  trackDsdLabel: string;
  /** Device setting hint on old connectors (force PCM for DSD). */
  forcePcmForDsd: boolean;
  trackSourceDsdHz: number | null;
  /** From outputPathBadge — Bit-Perfect / Shared suffix; omitted on decimated_pcm path. */
  outputPathSuffix: string;
}

/**
 * Local-native DSD badge — poller truth when available; never guesses DoP on old connectors.
 */
export function formatLocalNativeDsdBadge(input: FormatLocalNativeDsdBadgeInput): string {
  const pollerHz = input.exclusiveSourceDsdRateHz && input.exclusiveSourceDsdRateHz > 0
    ? input.exclusiveSourceDsdRateHz
    : null;
  const metaHz = input.trackSourceDsdHz && input.trackSourceDsdHz > 0
    ? input.trackSourceDsdHz
    : null;
  const dsdLabel = pollerHz
    ? dsdLabelFromRateHz(pollerHz)
    : (input.trackDsdLabel || (metaHz ? dsdLabelFromRateHz(metaHz) : 'DSD'));

  if (input.exclusiveDsdOutputMode === 'dop') {
    return `${dsdLabel} (DoP)${input.outputPathSuffix}`;
  }

  if (input.exclusiveDsdOutputMode === 'decimated_pcm' && pollerHz) {
    const pcmRate = decimatedPcmRateKhzLabel(pollerHz);
    return `${dsdLabel} → ${pcmRate} kHz PCM`;
  }

  if (input.forcePcmForDsd && metaHz) {
    const pcmRate = decimatedPcmRateKhzLabel(metaHz);
    return `${dsdLabel} → ${pcmRate} kHz PCM${input.outputPathSuffix}`;
  }

  return `${dsdLabel}${input.outputPathSuffix}`;
}
